/**
 * AgriDiagnosticFunnel — Funnel diagnostic CEE agricole dynamique.
 *
 * Multi-step intelligent : la séquence d'étapes s'adapte aux réponses.
 *
 * Étapes fixes (toujours) :
 *   orientation → siret → infra-overview → travaux → contact → result
 *
 * Étapes insérées dynamiquement entre `infra-overview` et `travaux` :
 *   • serres  (si déclaré "Oui" à l'overview)
 *   • elevage (idem)
 *   • stockage (idem)
 *
 * Au final l'utilisateur voit entre 6 et 9 étapes selon son profil.
 */

import { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft } from 'lucide-react';
import { easeStripe } from '../../../lib/motion';
import {
  trackFunnelStart,
  trackStepView,
  trackFunnelAbandon,
  type FunnelContext,
} from '../../../lib/tracking';
import { FunnelStepper } from '../../ui/funnel-stepper';
import { Step1Orientation } from './Step1Orientation';
import { Step2Siret } from './Step2Siret';
import { Step3InfraOverview } from './Step3InfraOverview';
import { Step3aSerres } from './Step3aSerres';
import { Step3bElevage } from './Step3bElevage';
import { Step3cStockage } from './Step3cStockage';
import { Step4Travaux } from './Step4Travaux';
import { Step5Contact } from './Step5Contact';
import { Step6Resultat } from './Step6Resultat';
import {
  AGRI_STEP_LABELS,
  getActiveIndex,
  getActiveSteps,
  useAgriStore,
} from './useAgriStore';

export function AgriDiagnosticFunnel() {
  const state = useAgriStore();
  const stepId = state.stepId;
  const dir = state.dir;
  const goBack = useAgriStore((s) => s.goBack);

  // Calcule dynamiquement les étapes visibles + l'index courant
  const activeSteps = getActiveSteps(state);
  const activeIndex = getActiveIndex(state);

  // Si l'étape courante n'est plus visible (ex: user a changé "serres oui → non"),
  // on revient à l'overview pour éviter d'être bloqué sur une étape disparue.
  useEffect(() => {
    if (!activeSteps.includes(stepId)) {
      useAgriStore.getState().setStep('infra-overview', -1);
    }
  }, [stepId, activeSteps]);

  const agriCtx: FunnelContext = {
    funnelId: 'agri_diagnostic',
    segment: 'agri',
    intent: 'cee',
    source: 'diagnostic',
  };

  // funnel_start au montage initial
  const agriDidStart = useRef(false);
  useEffect(() => {
    if (!agriDidStart.current) {
      agriDidStart.current = true;
      trackFunnelStart(agriCtx);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // step_view à chaque changement d'étape.
  const prevStep = useRef(stepId);
  useEffect(() => {
    if (stepId !== prevStep.current) {
      prevStep.current = stepId;
      trackStepView(agriCtx, stepId, activeIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepId, activeIndex]);

  // Abandon au déchargement
  useEffect(() => {
    const onUnload = () => {
      if (stepId !== 'result') {
        trackFunnelAbandon(agriCtx, stepId, activeIndex);
      }
    };
    window.addEventListener('pagehide', onUnload);
    return () => window.removeEventListener('pagehide', onUnload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepId, activeIndex]);

  // Scroll to top du funnel à chaque changement d'étape (utile sur mobile)
  // + focus du contenu de l'étape après la transition (a11y clavier/SR).
  const skippedFirstFocus = useRef(false);
  useEffect(() => {
    const el = document.getElementById('agri-funnel-top');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    if (!skippedFirstFocus.current) {
      skippedFirstFocus.current = true;
      return;
    }
    const t = window.setTimeout(() => {
      document.getElementById('agri-step-content')?.focus({ preventScroll: true });
    }, 300);
    return () => window.clearTimeout(t);
  }, [stepId]);

  const canGoBack = stepId !== 'orientation' && stepId !== 'result';

  // Steps mappés en format Stepper
  const stepperSteps = activeSteps.map((id) => ({
    id,
    label: AGRI_STEP_LABELS[id],
  }));

  return (
    <div
      id="agri-funnel-top"
      className="relative overflow-hidden rounded-2xl border border-stone-200/70 bg-white shadow-[0_8px_32px_-8px_rgba(10,10,10,.10)]"
    >
      {/* ── Header — stepper dynamique ────────────────────── */}
      <div className="border-b border-stone-200/80 bg-gradient-to-br from-stone-50/80 via-paper to-stone-50/40 px-5 py-4 md:px-8">
        <FunnelStepper steps={stepperSteps} current={activeIndex} />
      </div>

      {/* ── Body — contenu de l'étape ─────────────────────── */}
      <div className="relative px-5 py-6 md:px-8 md:py-8">
        {canGoBack && (
          <button
            type="button"
            onClick={goBack}
            className="-ml-1 mb-5 inline-flex min-h-11 items-center gap-1 rounded-md px-1.5 py-1 text-[12px] font-medium text-stone-400 transition-colors hover:bg-stone-50 hover:text-stone-700"
          >
            <ChevronLeft className="h-3.5 w-3.5" strokeWidth={2} />
            Retour
          </button>
        )}

        <AnimatePresence mode="wait" custom={dir} initial={false}>
          <motion.div
            key={stepId}
            id="agri-step-content"
            tabIndex={-1}
            className="outline-none"
            custom={dir}
            initial={{ opacity: 0, x: dir * 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: dir * -24 }}
            transition={{ duration: 0.28, ease: easeStripe }}
          >
            {stepId === 'orientation' && <Step1Orientation />}
            {stepId === 'siret' && <Step2Siret />}
            {stepId === 'infra-overview' && <Step3InfraOverview />}
            {stepId === 'serres' && <Step3aSerres />}
            {stepId === 'elevage' && <Step3bElevage />}
            {stepId === 'stockage' && <Step3cStockage />}
            {stepId === 'travaux' && <Step4Travaux />}
            {stepId === 'contact' && <Step5Contact />}
            {stepId === 'result' && <Step6Resultat />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
